import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarRange, Sparkles, Save, RefreshCw, Trash2, AlertTriangle, Filter, Edit3, Flag, Play } from 'lucide-react';
import { ModuleHero } from '../components/ModuleHero';
import { GuidedEmpty } from '../components/GuidedEmpty';
import { Modal } from '../components/Modal';
import { ConfirmDialog, EventWorkspaceSection, InputConfirmDialog } from '../components/PageTemplates';
import { useOverlayScrollLock } from '../hooks/useOverlayScrollLock';
import { useStore } from '../hooks/useStore';
import {
  setupStatus,
} from '../services/tournamentService';
import {
  listGames,
  listScheduleSlots,
  listRinksForTournament,
  ensureRinksForTournament,
  previewSchedule,
  commitSchedule,
  clearSchedule,
  updateGameSchedule,
} from '../services/schedulerService';
import { submitForfeitResult } from '../services/scoringService';
import { listTeams, listDivisions } from '../services/rosterService';
import type { ScheduleProposal } from '../services/schedulerEngine';
import { useActiveTournamentMaybe } from '../state/TournamentContext';

export function SchedulerPage() {
  const db = useStore((d) => d);
  const workspaceTournament = useActiveTournamentMaybe()?.tournament;
  const tournaments = useMemo(
    () => [...db.tournaments].filter((t) => t.status !== 'archived').sort((a, b) => b.startsAt.localeCompare(a.startsAt)),
    [db.tournaments],
  );
  const [tournamentId, setTournamentId] = useState<string>(() => workspaceTournament?.id ?? tournaments[0]?.id ?? '');
  const [proposal, setProposal] = useState<ScheduleProposal | null>(null);
  const [filterDivision, setFilterDivision] = useState<string>('');
  const [editingGameId, setEditingGameId] = useState<string>('');
  const [editStartsAt, setEditStartsAt] = useState('');
  const [editRinkId, setEditRinkId] = useState('');
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [forfeitGameId, setForfeitGameId] = useState('');
  const [forfeitWinner, setForfeitWinner] = useState('');

  const tournament = workspaceTournament ?? tournaments.find((t) => t.id === tournamentId);
  const isInWorkspace = Boolean(workspaceTournament);

  const games = tournament ? listGames(tournament.id) : [];
  const slots = tournament ? listScheduleSlots(tournament.id) : [];
  const slotById = useMemo(() => new Map(slots.map((s) => [s.id, s])), [slots]);
  const rinks = tournament ? listRinksForTournament(tournament.id) : [];
  const teams = tournament ? listTeams(tournament.id) : [];
  const divisions = tournament ? listDivisions(tournament.id) : [];

  const onGenerate = () => {
    if (!tournament) return;
    // Make sure there are some rinks (default 2 if venue has none).
    if (rinks.length === 0) ensureRinksForTournament(tournament.id, 2);
    setProposal(previewSchedule(tournament.id));
  };

  const onCommit = () => {
    if (!tournament || !proposal) return;
    commitSchedule(tournament.id, proposal);
    setProposal(null);
  };

  const onClear = () => {
    if (!tournament) return;
    setClearConfirmOpen(true);
  };

  const visibleGames = filterDivision
    ? games.filter((g) => g.divisionId === filterDivision)
    : games;
  const editingGame = games.find((game) => game.id === editingGameId);
  useOverlayScrollLock(Boolean(editingGame));

  return (
    <EventWorkspaceSection>
      <ModuleHero
        eyebrow="Operate"
        title="Smart Scheduler"
        description={`Generate a fair schedule, review warnings, and tweak before you publish. The engine respects rest, rinks, and your ${tournament?.format === 'season' ? 'season' : 'tournament'} window.`}
        icon={CalendarRange}
        accent="arc"
        status={proposal ? { label: 'Draft proposal — not saved', tone: 'warn' } : games.length > 0 ? { label: `${games.length} games scheduled`, tone: 'ok' } : undefined}
      >
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          {!isInWorkspace && (
            <select
              className="select"
              value={tournamentId}
              onChange={(e) => {
                setTournamentId(e.target.value);
                setProposal(null);
              }}
              style={{ maxWidth: 280 }}
            >
              <option value="">Select a season or tournament...</option>
              {tournaments.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} — {setupStatus(t).label}
                </option>
              ))}
            </select>
          )}
          <button className="btn btn--primary" onClick={onGenerate} disabled={!tournament || teams.length < 2}>
            <Sparkles size={14} /> Generate schedule
          </button>
          {games.length > 0 && (
            <button className="btn btn--ghost" onClick={onClear}>
              <Trash2 size={14} /> Clear saved
            </button>
          )}
        </div>
      </ModuleHero>

      {!tournament ? (
        <div className="empty">Pick a season or tournament above to get started.</div>
      ) : teams.length < 2 ? (
        <div className="note note--warn">
          Add at least two teams before generating - head over to the Teams tab.
        </div>
      ) : (
        <>
          {proposal && (
            <ProposalPanel
              proposal={proposal}
              teamsById={Object.fromEntries(teams.map((t) => [t.id, t]))}
              divisionsById={Object.fromEntries(divisions.map((d) => [d.id, d]))}
              rinksById={Object.fromEntries(rinks.map((r) => [r.id, r]))}
              onCommit={onCommit}
              onDiscard={() => setProposal(null)}
              onRegenerate={onGenerate}
            />
          )}

          {games.length > 0 && (
            <div className="card">
              <div className="card__head">
                <div>
                  <h3 className="card__title">Saved schedule</h3>
                  <p className="card__sub">{games.length} games across {rinks.length} rink{rinks.length === 1 ? '' : 's'}.</p>
                </div>
                <div className="row" style={{ gap: 8 }}>
                  <Filter size={14} color="var(--text-muted)" />
                  <select
                    className="select"
                    value={filterDivision}
                    onChange={(e) => setFilterDivision(e.target.value)}
                    style={{ maxWidth: 200 }}
                  >
                    <option value="">All divisions</option>
                    {divisions.map((d) => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <table className="table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Rink</th>
                    <th>Division</th>
                    <th>Match</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {visibleGames.map((g) => {
                    const slot = g.scheduleSlotId ? slotById.get(g.scheduleSlotId) : null;
                    const rink = slot ? rinks.find((r) => r.id === slot.rinkId) : null;
                    const home = teams.find((t) => t.id === g.homeTeamId);
                    const away = teams.find((t) => t.id === g.awayTeamId);
                    const div = divisions.find((d) => d.id === g.divisionId);
                    return (
                      <tr key={g.id}>
                        <td>{slot ? new Date(slot.startsAt).toLocaleString([], { weekday: 'short', hour: 'numeric', minute: '2-digit' }) : '—'}</td>
                        <td>{rink?.name ?? '—'}</td>
                        <td className="muted">{div?.name ?? ''}</td>
                        <td><strong>{home?.name}</strong> vs {away?.name}</td>
                        <td><span className="chip">{g.status.replace('_', ' ')}</span></td>
                        <td>
                          <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                            <button
                              className="btn btn--ghost btn--sm"
                              type="button"
                              onClick={() => {
                                setEditingGameId(g.id);
                                setEditStartsAt(slot ? toLocalInput(slot.startsAt) : '');
                                setEditRinkId(slot?.rinkId ?? rinks[0]?.id ?? '');
                              }}
                            >
                              <Edit3 size={12} /> Edit
                            </button>
                            <Link to={`/admin/scoring/${g.id}`} className="btn btn--ghost btn--sm">
                              <Play size={12} /> Score
                            </Link>
                            {home && away && g.status !== 'final' && g.status !== 'forfeit' && (
                              <button
                                className="btn btn--ghost btn--sm"
                                type="button"
                                onClick={() => {
                                  setForfeitGameId(g.id);
                                  setForfeitWinner('');
                                }}
                              >
                                <Flag size={12} /> Forfeit
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <Modal
            open={Boolean(editingGame)}
            onClose={() => setEditingGameId('')}
            title="Edit game time"
            subtitle="Move this game to a different rink or start time. Scoring and assignments stay attached."
            footer={
              <>
                <button className="btn btn--ghost" onClick={() => setEditingGameId('')}>Cancel</button>
                <button
                  className="btn btn--primary"
                  onClick={() => {
                    if (!editingGame || !editStartsAt || !editRinkId) return;
                    updateGameSchedule(editingGame.id, { startsAt: fromLocalInput(editStartsAt), rinkId: editRinkId });
                    setEditingGameId('');
                  }}
                >
                  Save change
                </button>
              </>
            }
          >
            <div className="stack">
              <label className="field">
                <span className="field__label">Start time</span>
                <input className="input" type="datetime-local" value={editStartsAt} onChange={(event) => setEditStartsAt(event.target.value)} />
              </label>
              <label className="field">
                <span className="field__label">Rink</span>
                <select className="select" value={editRinkId} onChange={(event) => setEditRinkId(event.target.value)}>
                  {rinks.map((rink) => <option key={rink.id} value={rink.id}>{rink.name}</option>)}
                </select>
              </label>
            </div>
          </Modal>

          {games.length === 0 && !proposal && (
            <GuidedEmpty
              icon={CalendarRange}
              title="No schedule yet"
              body="Generate a schedule after your event has teams and rinks. The saved schedule feeds scoring, standings, public pages, and calendar exports."
            />
          )}
          <ConfirmDialog
            open={clearConfirmOpen}
            title="Clear the saved schedule?"
            body="Crew assignments will stay if their games are kept."
            confirmLabel="Clear schedule"
            tone="danger"
            onCancel={() => setClearConfirmOpen(false)}
            onConfirm={() => {
              if (tournament) clearSchedule(tournament.id);
              setClearConfirmOpen(false);
            }}
          />
          <InputConfirmDialog
            open={Boolean(forfeitGameId)}
            title="Submit forfeit result"
            body={forfeitDialogBody(games, teams, forfeitGameId)}
            label="Winning team"
            value={forfeitWinner}
            placeholder="Type team name or short code"
            confirmLabel="Submit forfeit"
            onValueChange={setForfeitWinner}
            onCancel={() => {
              setForfeitGameId('');
              setForfeitWinner('');
            }}
            onConfirm={() => {
              const game = games.find((row) => row.id === forfeitGameId);
              const home = game ? teams.find((team) => team.id === game.homeTeamId) : undefined;
              const away = game ? teams.find((team) => team.id === game.awayTeamId) : undefined;
              const normalized = forfeitWinner.trim().toLowerCase();
              if (game && home && (normalized === home.shortCode.toLowerCase() || normalized === home.name.toLowerCase())) {
                submitForfeitResult({ gameId: game.id, winningTeamId: home.id });
              }
              if (game && away && (normalized === away.shortCode.toLowerCase() || normalized === away.name.toLowerCase())) {
                submitForfeitResult({ gameId: game.id, winningTeamId: away.id });
              }
              setForfeitGameId('');
              setForfeitWinner('');
            }}
          />
        </>
      )}
    </EventWorkspaceSection>
  );
}

function forfeitDialogBody(games: ReturnType<typeof listGames>, teams: ReturnType<typeof listTeams>, gameId: string): string {
  const game = games.find((row) => row.id === gameId);
  const home = game ? teams.find((team) => team.id === game.homeTeamId) : undefined;
  const away = game ? teams.find((team) => team.id === game.awayTeamId) : undefined;
  if (!home || !away) return 'Enter the winning team for this forfeit.';
  return `Type "${home.shortCode}" or "${away.shortCode}" to choose the official winner.`;
}

function toLocalInput(value?: string): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 16);
}

function fromLocalInput(value: string): string {
  return new Date(value).toISOString();
}

function ProposalPanel({
  proposal,
  teamsById,
  divisionsById,
  rinksById,
  onCommit,
  onDiscard,
  onRegenerate,
}: {
  proposal: ScheduleProposal;
  teamsById: Record<string, { name: string }>;
  divisionsById: Record<string, { name: string }>;
  rinksById: Record<string, { name: string }>;
  onCommit: () => void;
  onDiscard: () => void;
  onRegenerate: () => void;
}) {
  return (
    <div className="card card--accent">
      <div className="card__head">
        <div>
          <h3 className="card__title">
            <Sparkles size={16} style={{ verticalAlign: -2, marginRight: 6 }} />
            Draft proposal
          </h3>
          <p className="card__sub">{proposal.games.length} games · {proposal.warnings.length} warning{proposal.warnings.length === 1 ? '' : 's'}</p>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button className="btn btn--ghost btn--sm" onClick={onRegenerate}>
            <RefreshCw size={14} /> Regenerate
          </button>
          <button className="btn btn--ghost btn--sm" onClick={onDiscard}>Discard</button>
          <button className="btn btn--primary btn--sm" onClick={onCommit}>
            <Save size={14} /> Save schedule
          </button>
        </div>
      </div>

      {proposal.warnings.length > 0 && (
        <div className="stack" style={{ marginBottom: 14 }}>
          {proposal.warnings.slice(0, 8).map((w, i) => (
            <div key={i} className="note note--warn">
              <AlertTriangle size={14} style={{ verticalAlign: -2, marginRight: 6 }} />
              {w.message}
            </div>
          ))}
          {proposal.warnings.length > 8 && (
            <div className="muted" style={{ fontSize: 12 }}>+{proposal.warnings.length - 8} more warnings</div>
          )}
        </div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table className="table">
          <thead>
            <tr>
              <th>Time</th>
              <th>Rink</th>
              <th>Division</th>
              <th>Match</th>
            </tr>
          </thead>
          <tbody>
            {proposal.games.slice(0, 80).map((g) => {
              const slot = proposal.slots.find((s) => s.id === g.scheduleSlotId);
              return (
                <tr key={g.id}>
                  <td>{slot ? new Date(slot.startsAt).toLocaleString([], { weekday: 'short', hour: 'numeric', minute: '2-digit' }) : '—'}</td>
                  <td>{slot ? rinksById[slot.rinkId]?.name ?? '—' : '—'}</td>
                  <td className="muted">{divisionsById[g.divisionId]?.name ?? ''}</td>
                  <td>
                    <strong>{teamsById[g.homeTeamId]?.name}</strong> vs {teamsById[g.awayTeamId]?.name}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {proposal.games.length > 80 && (
          <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
            Showing first 80 of {proposal.games.length} games.
          </div>
        )}
      </div>
    </div>
  );
}
